import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Info, PhoneCall, Users, Ruler, Waves, Trees, Home, Zap, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { mediaUrl } from "@/lib/api";
import { BACKYARDS, SAUNA_SCENES } from "@/backyardData";
import { BuildForm } from "@/components/showroom/BuildForm";
import { SHOWROOM } from "@/constants/testIds";

const Badge = ({ icon: Icon, children }) =>
  children ? (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-slate-200">
      <Icon size={13} className="text-cyan-300" /> {children}
    </span>
  ) : null;

const ColourGroup = ({ title, items, sel, onSel }) =>
  items.length ? (
    <div className="mt-3">
      <div className="mb-1.5 flex items-center justify-between text-xs">
        <span className="uppercase tracking-widest text-slate-500">{title}</span>
        <span className="text-slate-300">{items.find((c) => c.slug === sel)?.name}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((c) => (
          <button
            key={c.slug}
            onClick={() => onSel(c.slug)}
            title={c.name}
            data-testid={`colour-${title.toLowerCase()}-${c.slug}`}
            className={`h-8 w-8 overflow-hidden rounded-full border-2 transition-all ${sel === c.slug ? "border-cyan-400 ring-2 ring-cyan-400/30" : "border-white/15 hover:border-white/40"}`}
          >
            <span className="block h-full w-full" style={{ backgroundColor: c.hex }} />
          </button>
        ))}
      </div>
    </div>
  ) : null;

export const SpaDetailPanel = ({ spa, onClose, onCta }) => {
  const [yard, setYard] = useState(null);
  const [plusOn, setPlusOn] = useState(false);
  const [selectOn, setSelectOn] = useState(false);
  const [config, setConfig] = useState(null);
  const [speed, setSpeed] = useState(null);
  const [shellSlug, setShellSlug] = useState(null);
  const [cabSlug, setCabSlug] = useState(null);
  const [trimSlug, setTrimSlug] = useState(null);
  const [galleryImg, setGalleryImg] = useState(null);
  const [showBuild, setShowBuild] = useState(false);
  useEffect(() => {
    setYard(null);
    setPlusOn(false);
    setSelectOn(false);
    setConfig(spa?.configs?.[0] || null);
    setSpeed(spa?.speeds?.[0] || null);
    setShellSlug(spa?.colour_swap ? "white-marble" : null);
    setCabSlug(spa?.colour_swap ? "timber" : null);
    setTrimSlug(spa?.colour_swap ? "charcoal" : null);
    setGalleryImg(null);
  }, [spa?.id, spa?.configs, spa?.speeds, spa?.colour_swap]);

  const colourImg =
    spa?.colour_swap && shellSlug && cabSlug && trimSlug
      ? mediaUrl(`/api/media/${spa.colour_dir || "cyc"}/${spa.colour_key}_${shellSlug}_${cabSlug}_${trimSlug}.webp`)
      : null;
  const mainImg = galleryImg || colourImg || (spa?.image_url ? mediaUrl(spa.image_url) : null);
  const angleGallery = (spa?.gallery || []).find((g) => g.includes("angle"));
  const placementSrc = spa?.placement_image || angleGallery || spa?.image_url;
  const placementImg = galleryImg || colourImg || (placementSrc ? mediaUrl(placementSrc) : null);
  const onImgErr = (e) => {
    if (spa?.image_url && !e.currentTarget.dataset.fb) {
      e.currentTarget.dataset.fb = "1";
      e.currentTarget.src = mediaUrl(spa.image_url);
    }
  };

  const displaySpecs = (spa?.specs || []).map((s) => {
    if (!(plusOn && spa?.has_plus)) return s;
    if (s.label === "Massage pumps") return { ...s, value: "2 x 3HP" };
    if (s.label === "Power supply") return { ...s, value: "240V · 24A load-shed / 36A fully loaded" };
    return s;
  });
  const hasSpaToggles = spa && (spa.has_plus || (spa.select_pack || []).length > 0);
  const hasUrbanSelectors = spa && (spa.configs || []).length > 0 && (spa.speeds || []).length > 0;
  const isSauna = spa?.category === "sauna";
  const scenes = isSauna ? SAUNA_SCENES : BACKYARDS;
  const sceneHeading = isSauna ? "See this sauna in my home" : "See it in your backyard";
  const SceneIcon = isSauna ? Home : Trees;

  return (
    <AnimatePresence>
      {spa && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.aside
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col overflow-y-auto border-l border-white/10 bg-[#191b21]/95 backdrop-blur-xl no-scrollbar"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 240, damping: 28 }}
            data-testid={SHOWROOM.spaPanel}
          >
            <div className="relative h-64 shrink-0 bg-white/5">
              {yard ? (
                <>
                  <img src={yard.image} alt={yard.name} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#191b21] via-black/10 to-black/30" />
                  {mainImg ? (
                    <motion.img
                      key={`${yard.id}-${shellSlug}-${cabSlug}`}
                      initial={{ y: 12, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      src={placementImg}
                      onError={onImgErr}
                      alt={spa.name}
                      className="absolute bottom-4 left-4 w-40 object-contain drop-shadow-2xl"
                    />
                  ) : null}
                  <span className="absolute left-4 top-4 rounded-full bg-black/55 px-3 py-1 text-xs text-cyan-200 backdrop-blur-md">
                    Imagine it in your {yard.name}
                    {spa.colour_swap && shellSlug && cabSlug ? (
                      <span className="ml-1 text-amber-200">
                        · {(spa.shells || []).find((c) => c.slug === shellSlug)?.name} / {(spa.cabinets || []).find((c) => c.slug === cabSlug)?.name} / {(spa.trims || []).find((c) => c.slug === trimSlug)?.name}
                      </span>
                    ) : null}
                  </span>
                </>
              ) : mainImg ? (
                <img src={mainImg} onError={onImgErr} alt={spa.name} className="h-full w-full object-contain p-4" />
              ) : null}
              <button
                onClick={onClose}
                className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-black/40 text-white backdrop-blur-md transition-colors hover:bg-black/70"
                data-testid={SHOWROOM.spaPanelClose}
              >
                <X size={18} />
              </button>
            </div>

            {/* Backyard swapper */}
            <div className="border-b border-white/5 px-6 py-4">
              <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
                <SceneIcon size={13} className="text-cyan-300" /> {sceneHeading}
              </div>
              <div className="flex gap-2 overflow-x-auto no-scrollbar">
                <button
                  onClick={() => setYard(null)}
                  data-testid="backyard-studio"
                  className={`shrink-0 overflow-hidden rounded-lg border text-[10px] transition-colors ${!yard ? "border-cyan-400" : "border-white/10 opacity-70 hover:opacity-100"}`}
                >
                  <div className="flex h-12 w-16 items-center justify-center bg-white/5 text-slate-300">Studio</div>
                </button>
                {scenes.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => setYard(b)}
                    data-testid={`backyard-${b.id}`}
                    className={`relative h-12 w-16 shrink-0 overflow-hidden rounded-lg border transition-colors ${yard?.id === b.id ? "border-cyan-400" : "border-white/10 opacity-70 hover:opacity-100"}`}
                    title={b.name}
                  >
                    <img src={b.image} alt={b.name} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-1 flex-col p-6">
              {spa.brand ? (
                <span className="text-xs uppercase tracking-widest text-amber-400">{spa.brand}</span>
              ) : null}
              <h2 className="mt-1 font-display text-3xl font-semibold tracking-tight">{spa.name}</h2>
              {spa.tagline ? <p className="mt-1 text-sm text-cyan-300">{spa.tagline}</p> : null}

              <div className="mt-4 flex flex-wrap gap-2">
                <Badge icon={Users}>{spa.seats ? `${spa.seats} Person` : null}</Badge>
                <Badge icon={Ruler}>{spa.size_label || spa.dimensions}</Badge>
                <Badge icon={Waves}>{spa.swim_system || spa.zone}</Badge>
              </div>

              {spa.colour_swap ? (
                <div className="mt-5" data-testid="cyclone-colour-picker">
                  <p className="text-xs uppercase tracking-widest text-slate-500">Build your colour</p>
                  <ColourGroup title="Shell" items={spa.shells || []} sel={shellSlug} onSel={(s) => { setShellSlug(s); setGalleryImg(null); }} />
                  <ColourGroup title="Cabinet" items={spa.cabinets || []} sel={cabSlug} onSel={(s) => { setCabSlug(s); setGalleryImg(null); }} />
                  <ColourGroup title="Trim" items={spa.trims || []} sel={trimSlug} onSel={(s) => { setTrimSlug(s); setGalleryImg(null); }} />
                  <p className="mt-2 text-[11px] text-slate-500">Preview updates above · representative render</p>
                  <button
                    type="button"
                    onClick={() => setShowBuild(true)}
                    data-testid={SHOWROOM.buildOpen}
                    className="mt-3 inline-flex items-center gap-2 rounded-full bg-amber-400/15 px-4 py-2 text-sm font-semibold text-amber-200 ring-1 ring-amber-400/30 transition-colors hover:bg-amber-400/25"
                  >
                    <Sparkles size={15} /> Save my build & email it to me
                  </button>
                </div>
              ) : null}

              {spa.description ? (
                <p className="mt-4 text-sm leading-relaxed text-slate-400">{spa.description}</p>
              ) : null}

              {spa.gallery?.length ? (
                <div className="mt-5" data-testid="spa-gallery">
                  <p className="mb-2 text-xs uppercase tracking-widest text-slate-500">Gallery</p>
                  <div className="flex gap-2 overflow-x-auto no-scrollbar">
                    <button
                      onClick={() => setGalleryImg(null)}
                      data-testid="gallery-thumb-main"
                      className={`h-16 w-20 shrink-0 overflow-hidden rounded-lg border transition-colors ${!galleryImg ? "border-cyan-400" : "border-white/10 opacity-70 hover:opacity-100"}`}
                    >
                      <img src={mediaUrl(spa.image_url)} alt="main view" className="h-full w-full object-cover" />
                    </button>
                    {spa.gallery.map((g, i) => {
                      const u = mediaUrl(g);
                      return (
                        <button
                          key={i}
                          onClick={() => setGalleryImg(u)}
                          data-testid={`gallery-thumb-${i}`}
                          className={`h-16 w-20 shrink-0 overflow-hidden rounded-lg border transition-colors ${galleryImg === u ? "border-cyan-400" : "border-white/10 opacity-70 hover:opacity-100"}`}
                        >
                          <img src={u} alt={`view ${i + 1}`} className="h-full w-full object-cover" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {hasSpaToggles ? (
                <div className="mt-6 space-y-2">
                  <p className="text-xs uppercase tracking-widest text-slate-500">Configure your spa</p>
                  {spa.has_plus ? (
                    <button
                      onClick={() => setPlusOn((v) => !v)}
                      data-testid={SHOWROOM.togglePlus}
                      className={`flex w-full items-center justify-between rounded-xl border p-3 text-left transition-colors ${plusOn ? "border-cyan-400 bg-cyan-500/10" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"}`}
                    >
                      <span className="flex items-center gap-2.5">
                        <Zap size={16} className="text-cyan-300" />
                        <span>
                          <span className="block text-sm font-medium text-slate-200">PLUS upgrade</span>
                          <span className="block text-xs text-slate-500">Adds a 2nd jet pump (2 total)</span>
                        </span>
                      </span>
                      <span className={`h-5 w-9 rounded-full p-0.5 transition-colors ${plusOn ? "bg-cyan-400" : "bg-white/15"}`}>
                        <span className={`block h-4 w-4 rounded-full bg-white transition-transform ${plusOn ? "translate-x-4" : ""}`} />
                      </span>
                    </button>
                  ) : null}
                  {(spa.select_pack || []).length ? (
                    <button
                      onClick={() => setSelectOn((v) => !v)}
                      data-testid={SHOWROOM.toggleSelect}
                      className={`flex w-full items-center justify-between rounded-xl border p-3 text-left transition-colors ${selectOn ? "border-amber-400 bg-amber-400/10" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"}`}
                    >
                      <span className="flex items-center gap-2.5">
                        <Package size={16} className="text-amber-300" />
                        <span>
                          <span className="block text-sm font-medium text-slate-200">Select pack</span>
                          <span className="block text-xs text-slate-500">Ozone &amp; UV, backlit jets, aux touchpad, Bluetooth</span>
                        </span>
                      </span>
                      <span className={`h-5 w-9 rounded-full p-0.5 transition-colors ${selectOn ? "bg-amber-400" : "bg-white/15"}`}>
                        <span className={`block h-4 w-4 rounded-full bg-white transition-transform ${selectOn ? "translate-x-4" : ""}`} />
                      </span>
                    </button>
                  ) : null}
                  {selectOn && (spa.select_pack || []).length ? (
                    <ul className="mt-1 grid grid-cols-1 gap-1.5 rounded-xl border border-amber-400/20 bg-amber-400/[0.05] p-3" data-testid="spa-select-inclusions">
                      {spa.select_pack.map((f, i) => (
                        <li key={i} className="flex items-center gap-2 text-xs text-amber-100">
                          <span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> {f}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}

              {hasUrbanSelectors ? (
                <div className="mt-6 space-y-3">
                  <p className="text-xs uppercase tracking-widest text-slate-500">Build your Urban Pool</p>
                  <div>
                    <div className="mb-1.5 text-xs text-slate-400">Configuration</div>
                    <div className="flex flex-wrap gap-2">
                      {spa.configs.map((c) => (
                        <button
                          key={c}
                          onClick={() => setConfig(c)}
                          data-testid={`${SHOWROOM.configPrefix}${c.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                          className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${config === c ? "border-cyan-400 bg-cyan-500/15 text-cyan-200" : "border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"}`}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="mb-1.5 text-xs text-slate-400">Swim speed</div>
                    <div className="flex flex-wrap gap-2">
                      {spa.speeds.map((sp) => (
                        <button
                          key={sp}
                          onClick={() => setSpeed(sp)}
                          data-testid={`${SHOWROOM.speedPrefix}${sp.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                          className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${speed === sp ? "border-cyan-400 bg-cyan-500/15 text-cyan-200" : "border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"}`}
                        >
                          {sp}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-sm text-slate-300" data-testid="urban-build-summary">
                    Your build: <span className="text-cyan-300">{spa.size_label} · {config} · {speed}</span>
                  </div>
                </div>
              ) : null}

              {displaySpecs.length ? (
                <div className="mt-6 grid grid-cols-2 gap-3">
                  {displaySpecs.map((s, i) => (
                    <div key={i} className="rounded-xl border border-white/5 bg-white/[0.03] p-3">
                      <div className="text-[11px] uppercase tracking-wide text-slate-500">{s.label}</div>
                      <div className="mt-0.5 text-sm font-medium text-slate-200">{s.value}</div>
                    </div>
                  ))}
                </div>
              ) : null}

              {spa.features?.length ? (
                <div className="mt-6">
                  <p className="mb-2 text-xs uppercase tracking-widest text-slate-500">Features</p>
                  <ul className="grid grid-cols-1 gap-1.5">
                    {spa.features.map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                        <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {spa.upgrade_packs?.length ? (
                <div className="mt-6">
                  <p className="mb-2 text-xs uppercase tracking-widest text-slate-500">Upgrade packs & options</p>
                  <div className="space-y-2">
                    {spa.upgrade_packs.map((u, i) => (
                      <div key={i} className="rounded-lg border border-white/5 bg-white/[0.03] p-3">
                        <div className="text-sm font-medium text-slate-200">{u.name}</div>
                        {u.description ? <div className="mt-0.5 text-xs text-slate-500">{u.description}</div> : null}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="mt-8 space-y-2">
                <p className="pb-1 text-xs uppercase tracking-widest text-slate-500">Picture it in your backyard</p>
                <Button
                  onClick={() => onCta("interest", spa)}
                  className="w-full justify-start gap-3 rounded-full bg-cyan-500 py-6 text-black hover:bg-cyan-400"
                  data-testid={SHOWROOM.ctaInterest}
                >
                  <Sparkles size={18} /> Express interest in this model
                </Button>
                <Button
                  onClick={() => onCta("info", spa)}
                  variant="outline"
                  className="w-full justify-start gap-3 rounded-full border-white/15 bg-white/5 py-6 text-white hover:bg-white/10"
                  data-testid={SHOWROOM.ctaInfo}
                >
                  <Info size={18} /> More information
                </Button>
                <Button
                  onClick={() => onCta("callback", spa)}
                  variant="outline"
                  className="w-full justify-start gap-3 rounded-full border-amber-400/30 bg-amber-400/10 py-6 text-amber-200 hover:bg-amber-400/20"
                  data-testid={SHOWROOM.ctaCallback}
                >
                  <PhoneCall size={18} /> Request a call back
                </Button>
              </div>
            </div>
          </motion.aside>
          {showBuild && (
            <BuildForm
              spa={spa}
              shellName={(spa.shells || []).find((c) => c.slug === shellSlug)?.name}
              cabinetName={(spa.cabinets || []).find((c) => c.slug === cabSlug)?.name}
              trimName={(spa.trims || []).find((c) => c.slug === trimSlug)?.name}
              renderUrl={spa.colour_swap && shellSlug && cabSlug && trimSlug ? `/api/media/${spa.colour_dir || "cyc"}/${spa.colour_key}_${shellSlug}_${cabSlug}_${trimSlug}.webp` : ""}
              onClose={() => setShowBuild(false)}
            />
          )}
        </>
      )}
    </AnimatePresence>
  );
};
